import { ChevronLeft, ChevronRight, Paperclip } from "lucide-react";

import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { dateToDateOnly } from "@/lib/format";
import type { AttendanceDay, AttendanceDayStatus } from "../../types";
import { dayWorkedMinutes, isFutureDate } from "../../utils/attendance";
import { hasOpenEntry, sortIntervals } from "../../utils/day";
import {
	dayLabel,
	EMPTY_VALUE,
	entryMinutes,
	formatClock,
	formatMinutes,
	formatSignedMinutes,
	isToday,
} from "../../utils/format";
import { isCurrentWeek, weekDates, weekLabel } from "../../utils/week";
import { IntervalLocation } from "../interval-location";

type RowStatus = AttendanceDayStatus | "in_progress" | "future";

const STATUS_META: Record<RowStatus, { label: string; variant: BadgeVariant }> =
	{
		present: { label: "Completo", variant: "success" },
		in_progress: { label: "Em andamento", variant: "default" },
		open: { label: "A registrar", variant: "secondary" },
		future: { label: "", variant: "ghost" },
		pending: { label: "Pendente", variant: "warning" },
		absent: { label: "Falta", variant: "destructive" },
		medical_leave: { label: "Atestado", variant: "info" },
		day_off: { label: "Folga", variant: "outline" },
		not_scheduled: { label: "Sem jornada", variant: "ghost" },
	};

function rowStatus(day: AttendanceDay | undefined): RowStatus {
	if (!day) return "open";
	if (day.status === "present" && hasOpenEntry(day.entries)) {
		return "in_progress";
	}
	if (day.status === "open" && isFutureDate(day.date)) return "future";
	return day.status;
}

export function WeekTable({
	reference,
	days,
	now,
	onPrevious,
	onNext,
}: {
	reference: Date;
	days: AttendanceDay[];
	now: Date;
	onPrevious: () => void;
	onNext: () => void;
}) {
	const byDate = new Map(days.map((day) => [day.date, day]));
	const dates = weekDates(reference);
	const atCurrentWeek = isCurrentWeek(reference);

	return (
		<Card className="presence-week">
			<CardHeader>
				<CardTitle>Esta semana</CardTitle>
				<CardDescription>{weekLabel(reference)}</CardDescription>
				<CardAction>
					<div className="presence-week__nav">
						<Button
							variant="outline"
							size="icon"
							aria-label="Semana anterior"
							onClick={onPrevious}
						>
							<ChevronLeft />
						</Button>
						<Button
							variant="outline"
							size="icon"
							aria-label="Próxima semana"
							disabled={atCurrentWeek}
							onClick={onNext}
						>
							<ChevronRight />
						</Button>
					</div>
				</CardAction>
			</CardHeader>
			<CardContent className="presence-week__content">
				<table className="presence-week__table">
					<thead>
						<tr>
							<th>Dia</th>
							<th>Entrada</th>
							<th>Saída</th>
							<th>Local</th>
							<th className="presence-week__total">Total</th>
							<th className="presence-week__total">Extras</th>
							<th>Status</th>
						</tr>
					</thead>
					{dates.map((date) => {
						const workDate = dateToDateOnly(date);
						const day = byDate.get(workDate);
						const dayEntries = sortIntervals(day?.entries ?? []);
						const status = rowStatus(day);
						const meta = STATUS_META[status];
						const showTotal = dayEntries.length > 1;
						const rowSpan = dayEntries.length + (showTotal ? 1 : 0);
						const overtime = day ? day.totals.overtimeMinutes : 0;
						const hasAttachments = (day?.record?.attachments.length ?? 0) > 0;
						const statusCell = (
							<span className="presence-week__status">
								{meta.label && (
									<Badge variant={meta.variant}>{meta.label}</Badge>
								)}
								{hasAttachments && (
									<Paperclip
										className="presence-week__clip"
										aria-label="Com anexo"
									/>
								)}
							</span>
						);
						return (
							<tbody
								key={workDate}
								className="presence-week__day"
								data-work-date={workDate}
								data-status={status}
								data-kind={day?.kind}
								data-today={isToday(date) ? "true" : undefined}
							>
								{dayEntries.length === 0 && (
									<tr data-part="empty">
										<td className="presence-week__day-label">
											{dayLabel(date)}
										</td>
										<td className="presence-week__time">{EMPTY_VALUE}</td>
										<td className="presence-week__time">{EMPTY_VALUE}</td>
										<td className="presence-week__location">{EMPTY_VALUE}</td>
										<td className="presence-week__total">{EMPTY_VALUE}</td>
										<td className="presence-week__overtime">
											{formatSignedMinutes(overtime)}
										</td>
										<td>{statusCell}</td>
									</tr>
								)}
								{dayEntries.map((entry, index) => (
									<tr key={entry.id} data-part="interval">
										{index === 0 && (
											<td
												className="presence-week__day-label"
												rowSpan={rowSpan}
											>
												{dayLabel(date)}
											</td>
										)}
										<td className="presence-week__time">
											{formatClock(entry.clockedInAt)}
										</td>
										<td className="presence-week__time">
											{entry.clockedOutAt
												? formatClock(entry.clockedOutAt)
												: "em curso"}
										</td>
										<td className="presence-week__location">
											<IntervalLocation entry={entry} />
										</td>
										<td className="presence-week__total">
											{formatMinutes(entryMinutes(entry, now))}
										</td>
										{index === 0 && (
											<td className="presence-week__overtime" rowSpan={rowSpan}>
												{formatSignedMinutes(overtime)}
											</td>
										)}
										{index === 0 && <td rowSpan={rowSpan}>{statusCell}</td>}
									</tr>
								))}
								{showTotal && day && (
									<tr data-part="total">
										<td className="presence-week__total-label" colSpan={3}>
											Total do dia
										</td>
										<td className="presence-week__total">
											{formatMinutes(dayWorkedMinutes(day, now))}
										</td>
									</tr>
								)}
							</tbody>
						);
					})}
				</table>
			</CardContent>
		</Card>
	);
}

export function WeekTableSkeleton() {
	return (
		<Card className="presence-week">
			<CardHeader>
				<Skeleton className="presence-week__skeleton-title" />
				<Skeleton className="presence-week__skeleton-subtitle" />
			</CardHeader>
			<CardContent className="presence-week__skeleton-content">
				{[0, 1, 2, 3, 4].map((row) => (
					<Skeleton key={row} className="presence-week__skeleton-row" />
				))}
			</CardContent>
		</Card>
	);
}
